// pages/customers/CustomerList.jsx
import { useEffect, useState } from "react";
import { customersAPI }   from "../../services/api";
import { useRouter }      from "../../contexts/RouterContext";
import { useToast }       from "../../contexts/ToastContext";
import { usePagination }  from "../../hooks/usePagination";
import { Loading, Pagination, ConfirmModal, EmptyState } from "../../components/ui";
import { fmt }            from "../../utils/helpers";

export default function CustomerList() {
  const { navigate }          = useRouter();
  const toast                 = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  const load = () => {
    setLoading(true);
    customersAPI.list().then((data) => { setCustomers(data); setLoading(false); });
  };
  useEffect(load, []);

  const filtered = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.phone.includes(search) ||
    (c.address || "").toLowerCase().includes(search.toLowerCase())
  );

  const { paged, page, setPage, total, count } = usePagination(filtered, 8);

  // Compute order count per customer (in-memory join)
  const orderCount = (id) =>
    customers.length > 0 ? /* rely on same DB instance */ window._DB_ORDER_COUNT?.(id) ?? 0 : 0;

  const handleDelete = async () => {
    setDeleting(true);
    await customersAPI.delete(deleteTarget.id);
    toast(`"${deleteTarget.name}" deleted`);
    load();
    setDeleteTarget(null);
    setDeleting(false);
  };

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-sub">{customers.length} customer{customers.length !== 1 ? "s" : ""} registered</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => navigate("customer-add")}>
            ＋ Add Customer
          </button>
        </div>
      </div>

      <div className="card">
        {/* Search bar */}
        <div className="card-header">
          <div className="search-bar" style={{ flex: 1, maxWidth: 340 }}>
            <span className="search-icon">🔍</span>
            <input
              className="form-input"
              placeholder="Search by name, phone or address…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {search && (
            <button className="btn btn-ghost btn-sm" onClick={() => setSearch("")}>✕ Clear</button>
          )}
        </div>

        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer Name</th>
                    <th>Phone Number</th>
                    <th>Address</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <EmptyState
                          icon="👥"
                          title={search ? "No customers match your search" : "No customers yet"}
                          subtitle={!search ? "Click 'Add Customer' to get started" : undefined}
                        />
                      </td>
                    </tr>
                  ) : (
                    paged.map((c, i) => (
                      <tr key={c.id}>
                        <td className="td-muted" style={{ fontSize: 12 }}>{(page - 1) * 8 + i + 1}</td>
                        <td>
                          <span
                            style={{ fontWeight: 600, cursor: "pointer", color: "var(--gold)" }}
                            onClick={() => navigate("customer-detail", { id: c.id })}
                          >
                            {c.name}
                          </span>
                        </td>
                        <td className="td-muted">{c.phone}</td>
                        <td
                          className="td-muted"
                          style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {c.address || "—"}
                        </td>
                        <td className="td-muted">{fmt(c.createdAt)}</td>
                        <td>
                          <div className="td-actions">
                            <button className="btn btn-ghost btn-sm" onClick={() => navigate("customer-detail", { id: c.id })}>View</button>
                            {/* <button className="btn btn-ghost btn-sm" onClick={() => navigate("customer-edit",   { id: c.id })}>Edit</button> */}
                            {/* <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(c)}>Delete</button> */}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination page={page} total={total} setPage={setPage} count={count} perPage={8} />
          </>
        )}
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete Customer"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
      />
    </div>
  );
}
